import React from 'react';
import { Modal, Button, Tabs, Descriptions, Space, Tag, Table, Card, Badge, Typography, message } from 'antd';
import { FaUserAlt, FaHistory, FaMapMarkerAlt, FaEye } from 'react-icons/fa';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const { TabPane } = Tabs;
const { Text } = Typography;

const CustomerDetailModal = ({ 
  visible, 
  customer, 
  statusOptions, 
  tagOptions, 
  onClose 
}) => {
  const navigate = useNavigate()
  if (!customer) return null;

  // Format order history data from API response
  const orderHistory = customer.orders?.map(order => ({
    orderId: order.order_id,
    date: order.createdAt,
    total: order.totalAmount,
    status: order.orderStatus,
    paymentStatus: order.paymentStatus,
    paymentMethod: order.paymentMethod,
    items: order.order_items
  }));

  // Format addresses data from API response
  const addresses = customer.shipping_address?.map(address => ({
    type: address.addressType || 'Shipping',
    address: address.streetAddress,
    apartment: address.apartment,
    city: address.city,
    state: address.province,
    zip: address.postalCode,
    country: address.country,
    isDefault: address.isDefault,
    phone: address.phone
  }));

  // Get phone number from either shipping address or first order's customer info
  const getCustomerPhone = () => {
    // Try shipping address first
    if (customer.shipping_address?.[0]?.phone) {
      return customer.shipping_address[0].phone;
    }
    // Then try first order's customer info
    if (customer.orders?.[0]?.customer_info?.phone) {
      return customer.orders[0].customer_info.phone;
    }
    return 'Not provided';
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <div className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center mr-3">
            <FaUserAlt />
          </div>
          <span>
            {`${customer.first_name} ${customer.last_name}`}
          </span>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="back" onClick={onClose}>
          Close
        </Button>
      ]}  
    >
      <Tabs defaultActiveKey="1">
        <TabPane 
          tab={
            <span className='flex gap-4 items-center'>
              <FaUserAlt /> Profile
            </span>
          } 
          key="1"
        >
          <div className="w-full overflow-x-auto">
            <Descriptions 
              bordered 
              column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
              layout="horizontal"
              size="middle"
            >
              <Descriptions.Item label="Full Name" span={1}>
                {`${customer.first_name} ${customer.last_name}`}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={1}>
                {customer.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone" span={1}>
                {getCustomerPhone()}
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={1}>
                <Tag color={statusOptions.find(s => s.value === customer.status)?.color}>
                  {customer.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Customer Since" span={1}>
                {moment(customer.createdAt).format("MMM DD, YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Total Orders" span={1}>
                {customer.orders?.length || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Tags" span={2}>
                {customer.tags && customer.tags.length > 0 ? (
                  <Space>
                    {customer.tags.map(tag => {
                      const tagOption = tagOptions.find(t => t.value === tag);
                      return (
                        <Tag color={tagOption?.color || "blue"} key={tag}>
                          {tag}
                        </Tag>
                      );
                    })}
                  </Space>
                ) : (
                  'No tags'
                )}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </TabPane>
        <TabPane 
          tab={
            <span className='flex gap-4 items-center'>
              <FaHistory /> Order History
            </span>
          } 
          key="2"
        >
          {orderHistory && orderHistory.length > 0 ? (
            <Table
              dataSource={orderHistory}
              rowKey="orderId"
              pagination={false}
              columns={[
                {
                  title: 'Order ID',
                  dataIndex: 'orderId',
                  key: 'orderId',
                },
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => moment(date).format("MMM DD, YYYY"),
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  render: (total) => `$${total.toFixed(2)}`,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => (
                    <Tag color={status === 'Delivered' ? 'green' : status === 'Unfulfilled' ? 'orange' : 'blue'}>
                      {status}
                    </Tag>
                  ),
                },
                {
                  title: 'Payment',
                  key: 'payment',
                  render: (_, record) => (
                    <Space>
                      <Tag color={record.paymentStatus === 'Paid' ? 'green' : 'orange'}>
                        {record.paymentStatus}
                      </Tag>
                      <span>{record.paymentMethod}</span>
                    </Space>
                  ),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_, record) => {
                    // Find the original order object to get the _id
                    const originalOrder = customer.orders?.find(order => order.order_id === record.orderId);
                    return (
                      <Button 
                        icon={<FaEye />} 
                        onClick={() => navigate(`/order-management/${originalOrder?._id}`)}
                      >
                        View
                      </Button>
                    );
                  },
                },
              ]}
            />
          ) : (
            <div className="text-center py-4">
              <Text type="secondary">No order history available</Text>
            </div>
          )}
        </TabPane>
        <TabPane   
          tab={
            <span className='flex gap-4 items-center'>
              <FaMapMarkerAlt /> Addresses
            </span>
          } 
          key="3"
        >
          {addresses && addresses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {addresses.map((address, index) => (
                <Card 
                  key={index} 
                  title={
                    <div className="flex justify-between items-center">
                      <span>{address.type} Address</span>
                      {address.isDefault && <Badge color="green" text="Default" />}
                    </div>
                  }
                  className="w-full"
                >
                  <p>{address.address}{address.apartment && `, ${address.apartment}`}</p>
                  <p>{`${address.city}, ${address.state} ${address.zip}`}</p>
                  <p>{address.country}</p>
                  {address.phone && <p>Phone: {address.phone}</p>}
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <Text type="secondary">No addresses saved</Text>
            </div>
          )}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default CustomerDetailModal;